import { Component, ElementRef, EventEmitter, Input, Output, signal, viewChild } from '@angular/core';

export const DOCUMENT_DESCRIPTION_MAX_LENGTH = 500;

/**
 * Description cell for documentation tables with inline edit. The description is optional, so an
 * empty value is emitted as null. Emits only when the value actually changes.
 */
@Component({
  selector: 'app-document-description-cell',
  template: `
    @if (isEditing()) {
      <div class="document-description-editor">
        <div class="document-description-field">
          <input
            #descriptionInput
            type="text"
            class="kt-input kt-input-sm"
            [value]="draft()"
            [attr.maxlength]="maxLength"
            [attr.aria-label]="'New description for ' + (documentLabel || 'document')"
            placeholder="Add a description"
            (input)="onDraftInput($any($event.target).value)"
            (keydown.enter)="$event.preventDefault(); save()"
            (keydown.escape)="cancel()"
          />
          <button type="button" class="kt-btn kt-btn-sm kt-btn-icon kt-btn-primary" (click)="save()" aria-label="Save description">
            <i class="ki-filled ki-check" aria-hidden="true"></i>
          </button>
          <button type="button" class="kt-btn kt-btn-sm kt-btn-icon kt-btn-outline" (click)="cancel()" aria-label="Cancel description edit">
            <i class="ki-filled ki-cross" aria-hidden="true"></i>
          </button>
        </div>
        @if (error()) {
          <span class="document-description-error">{{ error() }}</span>
        }
      </div>
    } @else {
      <div class="document-description-view">
        <span>{{ description || '-' }}</span>
        @if (saving) {
          <span class="document-description-spinner" role="status" aria-label="Updating description"></span>
        } @else {
          <button
            type="button"
            class="document-description-edit"
            [disabled]="disabled"
            (click)="startEditing()"
            [attr.aria-label]="'Edit description of ' + (documentLabel || 'document')"
            title="Edit description"
          >
            <i class="ki-filled ki-pencil" aria-hidden="true"></i>
          </button>
        }
      </div>
    }
  `,
  styles: `
    .document-description-view {
      display: inline-flex;
      align-items: center;
      gap: 0.375rem;
    }

    .document-description-edit {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 1.5rem;
      height: 1.5rem;
      flex-shrink: 0;
      border-radius: 0.375rem;
      color: var(--color-muted-foreground, #9ca3af);
      opacity: 0;
      transition: opacity 0.15s ease, color 0.15s ease, background-color 0.15s ease;
    }

    :host-context(tr:hover) .document-description-edit,
    .document-description-edit:focus-visible {
      opacity: 1;
    }

    .document-description-edit:hover:not(:disabled) {
      color: var(--color-primary, #1b84ff);
      background-color: var(--color-accent, rgba(0, 0, 0, 0.05));
    }

    .document-description-edit:disabled {
      cursor: not-allowed;
    }

    .document-description-editor {
      display: grid;
      gap: 0.25rem;
      min-width: 16rem;
    }

    .document-description-field {
      display: flex;
      align-items: center;
      gap: 0.375rem;
    }

    .document-description-field input {
      flex: 1;
      min-width: 8rem;
    }

    .document-description-error {
      font-size: 0.75rem;
      color: var(--color-destructive, #dc2626);
    }

    .document-description-spinner {
      width: 0.875rem;
      height: 0.875rem;
      border: 2px solid var(--color-primary, #1b84ff);
      border-right-color: transparent;
      border-radius: 9999px;
      animation: document-description-spin 0.8s linear infinite;
    }

    @keyframes document-description-spin {
      to {
        transform: rotate(360deg);
      }
    }
  `,
})
export class DocumentDescriptionCell {
  @Input() description: string | null | undefined = null;
  /** Used only for accessible labels, typically the filename. */
  @Input() documentLabel: string | null | undefined = null;
  @Input() disabled = false;
  @Input() saving = false;

  @Output() readonly descriptionChange = new EventEmitter<string | null>();

  protected readonly maxLength = DOCUMENT_DESCRIPTION_MAX_LENGTH;

  private readonly descriptionInput = viewChild<ElementRef<HTMLInputElement>>('descriptionInput');

  protected readonly isEditing = signal(false);
  protected readonly draft = signal('');
  protected readonly error = signal('');

  protected startEditing(): void {
    if (this.disabled || this.saving) {
      return;
    }

    this.draft.set(this.description ?? '');
    this.error.set('');
    this.isEditing.set(true);
    setTimeout(() => {
      const input = this.descriptionInput()?.nativeElement;
      input?.focus();
      input?.select();
    });
  }

  protected onDraftInput(value: string): void {
    this.draft.set(value);
    this.error.set('');
  }

  protected cancel(): void {
    this.isEditing.set(false);
    this.error.set('');
  }

  protected save(): void {
    const next = this.draft().trim();
    if (next.length > this.maxLength) {
      this.error.set(`Description cannot exceed ${this.maxLength} characters.`);
      return;
    }

    if (next === (this.description ?? '').trim()) {
      this.cancel();
      return;
    }

    this.isEditing.set(false);
    this.descriptionChange.emit(next || null);
  }
}
